import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { createBillingAddress } from "@store/actions/billingAddresses";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { closeCart, resetCheckout, setCheckoutStep, setPaymentInFlight } from "@store/reducers/cart";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ERoutes } from "@utils/constants";
import styled, { keyframes } from "styled-components";

import { getStripe } from "@/utils/stripe";

import {
  Body,
  BottomFade,
  DesktopHeader,
  FirstSectionLabel,
  PhoneHeader,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "./phoneTemplate";
import {
  BackBtn,
  ButtonGroup,
  FormRow,
  IconCircle,
  InputField,
  InputGroup,
  PrimaryBtn,
  SectionLabel,
  SlidePanelFooter,
  StepSubtitle,
  StepTitle,
} from "./styles";
import { CA_PROVINCES, COUNTRIES, COUNTRY_CODES, US_STATES } from "./types";


const STATE_REQUIRED_COUNTRIES = new Set(["United States", "Canada"]);

/** Poll the upgrade-order endpoint until the `invoice.paid` webhook has built
 *  the order behind the scenes (or we give up). Returns the order, or null on
 *  timeout. The wait happens on the Pay button. */
const pollUpgradeOrder = async (invoiceId: string): Promise<any | null> => {
  const started = Date.now();
  const MAX_WAIT_MS = 30000;
  while (Date.now() - started < MAX_WAIT_MS) {
    try {
      const r = await instance.get(`/stripe/subscriptions/upgrade-order/${encodeURIComponent(invoiceId)}`);
      if (r.data && (r.data.id || r.data._id)) return r.data;
    } catch { /* keep waiting */ }
    await new Promise((res) => setTimeout(res, 1500));
  }
  return null;
};

/** Fetch an already-built order (the inline $0 / dev path). */
const fetchOrder = async (id: string): Promise<any | null> => {
  try {
    const r = await instance.get(`/orders/${id}`);
    return r.data || null;
  } catch {
    return null;
  }
};

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;
const ShakeWrap = styled.div<{ $shake: boolean }>`animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled.span`
  display: inline-block; width: 14px; height: 14px; margin-right: 8px;
  /* flex:0 0 auto so the narrow button never compresses the circle into an oval. */
  flex: 0 0 auto; box-sizing: border-box;
  border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%;
  animation: ${spin} 0.7s linear infinite; vertical-align: -2px;
`;

/* Footer actions. Desktop keeps the original 1:2 Back:Pay split (untouched).
   On phone, Back shrinks to its content width and Pay flexes to fill, so the
   "Pay $X Now" label fits on one line (same treatment as UpgradeReviewView). */
const Actions = styled(ButtonGroup)`
  margin-top: 0;
  border-top: none;
  padding-top: 0;

  ${BackBtn} { flex: 1; }
  ${PrimaryBtn} { flex: 2; }

  @media (max-width: 600px) {
    ${BackBtn} {
      flex: 0 0 auto;
      width: auto;
      padding-left: 20px;
      padding-right: 20px;
    }
    ${PrimaryBtn} {
      flex: 1;
      width: auto;
      white-space: nowrap;
    }
  }
`;

interface BillingFields {
  firstName: string; lastName: string; email: string;
  address: string; addressLine2: string;
  city: string; stateRegion: string; zipCode: string; country: string;
  companyName: string; vatId: string;
}
const REQUIRED_FIELDS: (keyof BillingFields)[] = ["firstName", "lastName", "address", "city", "zipCode", "country"];

/** Inner form — sits inside <Elements>. Submits the new PM via Stripe Elements,
 *  optionally saves an updated billing address, then triggers the upgrade
 *  through /stripe/subscriptions/update which now charges via the new
 *  customer-default PM (set by /stripe/set-default-payment-method below). */
const UpgradePaymentForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { subscriptionUpgrade } = useAppSelector((state) => state.cart);
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;

  const [paying, setPaying] = useState(false);
  // Second spinner stage: flips on once the card is saved and we move to
  // charging + applying the upgrade on the backend, so the button reads
  // "Finalizing..." (more is happening) — matches the cart's Pay button.
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stable per-attempt idempotency key (see UpgradeReviewView) — reused across
  // Pay re-clicks so a re-click after a lost response can't double-charge.
  const idemKeyRef = useRef<string>("");
  // Auto-scroll payment errors into view (rendered at the bottom of the body).
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [error]);
  // Scroll affordance — this is a tall body (billing form + card element), so on
  // phone show a bottom fade + "Scroll ⌄" cue while there's more below the fold.
  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);
  const [ready, setReady] = useState(false);
  // `complete` fires from PaymentElement.onChange and is only true once the
  // user has fully filled the card / picked a Link card / etc. When Link
  // shows its half-state ("Use this card" button before OTP verification),
  // complete stays false so we keep the Pay button disabled.
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [shakeForm, setShakeForm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof BillingFields, boolean>>>({});

  const [billing, setBilling] = useState<BillingFields>({
    firstName: (user as any)?.user?.firstName || "",
    lastName: "",
    email: (user as any)?.user?.email || "",
    address: "", addressLine2: "", city: "", stateRegion: "",
    zipCode: "", country: "",
    companyName: "", vatId: "",
  });

  const hasVat = !!billing.vatId.trim();
  const hasCompany = !!billing.companyName.trim();
  const vatCompanyMismatch = (hasVat && !hasCompany) || (!hasVat && hasCompany);

  // Prefill the form from the user's default billing address so a typical
  // "change card only" flow doesn't make them re-type their address.
  const [billingLoaded, setBillingLoaded] = useState(false);
  useEffect(() => {
    if (!isAuthenticated || billingLoaded) return;
    setBillingLoaded(true);
    instance.get("/billing-addresses").then((resp) => {
      const addresses = resp.data || [];
      const defaultAddr = addresses.find((a: any) => a.isDefault) || addresses[0];
      if (defaultAddr) {
        setBilling((prev) => ({
          firstName: defaultAddr.firstName || prev.firstName,
          lastName: defaultAddr.lastName || prev.lastName,
          email: defaultAddr.email || prev.email || (user as any)?.user?.email || "",
          address: defaultAddr.street || prev.address,
          addressLine2: defaultAddr.addressLine2 || prev.addressLine2,
          city: defaultAddr.city || prev.city,
          stateRegion: defaultAddr.stateRegion || prev.stateRegion,
          zipCode: defaultAddr.zipCode || prev.zipCode,
          country: defaultAddr.country || prev.country,
          companyName: defaultAddr.companyName || prev.companyName,
          vatId: defaultAddr.vatId || prev.vatId,
        }));
      }
    }).catch(() => {});
  }, [isAuthenticated, billingLoaded, user]);

  const updateField = (field: keyof BillingFields, value: string) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements || paying || !subscriptionUpgrade) return;

    const errors: Partial<Record<keyof BillingFields, boolean>> = {};
    REQUIRED_FIELDS.forEach((f) => { if (!billing[f].trim()) errors[f] = true; });
    if (STATE_REQUIRED_COUNTRIES.has(billing.country) && !billing.stateRegion.trim()) errors.stateRegion = true;
    if (vatCompanyMismatch) {
      if (!hasCompany) errors.companyName = true;
      if (!hasVat) errors.vatId = true;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 500);
      return;
    }

    setPaying(true);
    dispatch(setPaymentInFlight(true));
    setError(null);

    try {
      // 1. Validate the Stripe Elements payment input.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || "Payment details invalid. Please review and try again.");
        setPaying(false);
        dispatch(setPaymentInFlight(false));
        return;
      }

      // 2. Ask the backend for a SetupIntent so we can attach the new PM
      //    to this customer.
      const setupResp = await instance.post("/stripe/setup-intent");
      const setupClientSecret: string | undefined = setupResp?.data?.clientSecret;
      if (!setupClientSecret) throw new Error("Could not start payment update");

      // 3. Confirm the SetupIntent — this saves the new payment method
      //    against the customer in Stripe's vault.
      const result = await stripe.confirmSetup({
        elements,
        clientSecret: setupClientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${ERoutes.AccountSubscription}`,
          payment_method_data: {
            billing_details: {
              name: `${billing.firstName} ${billing.lastName}`.trim(),
              email: billing.email || (user as any)?.user?.email || "",
              phone: "",
              address: {
                line1: billing.address,
                line2: billing.addressLine2 || undefined,
                city: billing.city,
                state: billing.stateRegion || "",
                postal_code: billing.zipCode,
                country: COUNTRY_CODES[billing.country] || "",
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "Failed to save card. Please try again.");
        setPaying(false);
        dispatch(setPaymentInFlight(false));
        return;
      }

      // Card saved — switch the button to its second stage ("Finalizing...")
      // while we charge the new default PM + apply the upgrade on the backend.
      setFinalizing(true);

      // 4. Promote the new PM to be the customer's default so the upgrade
      //    invoice (charge_automatically) uses it.
      const setupIntent = result.setupIntent;
      const pmId = setupIntent && (typeof setupIntent.payment_method === "string"
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id);
      if (pmId) {
        await instance.post("/stripe/set-default-payment-method", { paymentMethodId: pmId });
      }

      // 5. Save any billing-address edits so future receipts use them
      //    (dedup on the backend handles the no-change case).
      if (isAuthenticated) {
        await dispatch(createBillingAddress({
          firstName: billing.firstName,
          lastName: billing.lastName,
          street: billing.address,
          addressLine2: billing.addressLine2 || undefined,
          city: billing.city,
          stateRegion: billing.stateRegion || undefined,
          zipCode: billing.zipCode,
          country: billing.country,
          companyName: billing.companyName || undefined,
          vatId: billing.vatId || undefined,
          customerType: hasCompany && hasVat ? "business" : "private",
          isDefault: true,
        }) as any).catch(() => undefined);
      }

      // 6. Finally fire the upgrade — backend stages + charges the (now
      //    updated) default PM. The order + plan change are built by the
      //    invoice.paid webhook BEHIND THE SCENES (atomicity — the DB change
      //    happens only once Stripe confirms). We keep the button on
      //    "Finalizing..." while we poll for the built order, then navigate to
      //    a fully-rendered success page (no on-page loading). On timeout we
      //    land on the "being processed" page; the reconcile backstop finishes
      //    it within minutes. See docu/flows/payments/payment-atomicity.md.
      const { newChannels, newClaims } = subscriptionUpgrade;
      if (!idemKeyRef.current) idemKeyRef.current = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      const upgradeResp = await instance.post("/stripe/subscriptions/update", {
        channels: newChannels,
        claims: newClaims,
        idempotencyKey: idemKeyRef.current,
      });
      const orderId: string | null = upgradeResp?.data?.orderId || null;
      const stripeSub: string | null = upgradeResp?.data?.stripeSubscriptionId || null;
      const upgradeInvoiceId: string | null = upgradeResp?.data?.upgradeInvoiceId || null;

      const order = orderId
        ? await fetchOrder(orderId)                  // inline-built ($0 / dev)
        : upgradeInvoiceId
          ? await pollUpgradeOrder(upgradeInvoiceId)
          : null;

      dispatch(closeCart());
      setTimeout(() => dispatch(resetCheckout()), 350);
      if (order) {
        navigate(`${ERoutes.OrderSuccess}/${order.id || order._id}`, { state: { order } });
      } else if (upgradeInvoiceId) {
        navigate(`${ERoutes.OrderSuccess}?upgradeInvoice=${encodeURIComponent(upgradeInvoiceId)}`);
      } else if (stripeSub) {
        navigate(`${ERoutes.OrderSuccess}?stripeSub=${encodeURIComponent(stripeSub)}&type=subscription_addon`);
      } else {
        navigate(ERoutes.AccountSubscription);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
      setPaying(false);
      setFinalizing(false);
      dispatch(setPaymentInFlight(false));
    }
  }, [stripe, elements, paying, billing, subscriptionUpgrade, dispatch, navigate, isAuthenticated, user, hasCompany, hasVat, vatCompanyMismatch]);

  if (!subscriptionUpgrade) return null;

  const isAnnual = subscriptionUpgrade.billingPeriod === "annual";
  const cashCreditMonthly = Math.round(subscriptionUpgrade.oldTotal * (Math.max(0, subscriptionUpgrade.daysLeftInPeriod) / 30) * 100) / 100;
  const chargeTodayMonthly = Math.max(0, Math.round((subscriptionUpgrade.newTotal - cashCreditMonthly) * 100) / 100);
  const yearlyDiff = (subscriptionUpgrade.newTotal - subscriptionUpgrade.oldTotal) * 12;
  const proratedDiffAnnual = Math.round(yearlyDiff * (Math.max(0, subscriptionUpgrade.daysLeftInPeriod) / 365) * 100) / 100;
  const payAmount = isAnnual ? proratedDiffAnnual : chargeTodayMonthly;

  return (
    <>
      {/* PHONE: fixed header — centred title, no icon (✕ stays top-right). */}
      <PhoneHeader>
        <h2>Update Card &amp; Pay</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
        {/* DESKTOP/TABLET: original centred icon-circle header (hidden on phone). */}
        <DesktopHeader>
          <IconCircle>
            <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </IconCircle>
          <div>
            <StepTitle>Update Card & Pay</StepTitle>
            <StepSubtitle>Charge will use the new card and save it for future renewals</StepSubtitle>
          </div>
        </DesktopHeader>

        <ShakeWrap $shake={shakeForm}>
          <FirstSectionLabel><span>Billing Address</span></FirstSectionLabel>
          <FormRow>
            <InputGroup><InputField type="text" placeholder="First name" value={billing.firstName} onChange={(e) => updateField("firstName", e.target.value)} $error={!!fieldErrors.firstName} /></InputGroup>
            <InputGroup><InputField type="text" placeholder="Last name" value={billing.lastName} onChange={(e) => updateField("lastName", e.target.value)} $error={!!fieldErrors.lastName} /></InputGroup>
          </FormRow>
          <InputGroup style={{ marginTop: "8px" }}>
            <InputField type="text" placeholder="Street address" value={billing.address} onChange={(e) => updateField("address", e.target.value)} $error={!!fieldErrors.address} />
          </InputGroup>
          <InputGroup style={{ marginTop: "8px" }}>
            <InputField type="text" placeholder="Apartment, suite, etc. (optional)" value={billing.addressLine2} onChange={(e) => updateField("addressLine2", e.target.value)} />
          </InputGroup>
          <FormRow style={{ marginTop: "8px" }}>
            <InputGroup><InputField type="text" placeholder="City" value={billing.city} onChange={(e) => updateField("city", e.target.value)} $error={!!fieldErrors.city} /></InputGroup>
            <InputGroup><InputField type="text" placeholder="ZIP / Postal code" value={billing.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} $error={!!fieldErrors.zipCode} /></InputGroup>
          </FormRow>
          <InputGroup style={{ marginTop: "8px", marginBottom: STATE_REQUIRED_COUNTRIES.has(billing.country) ? "8px" : "20px" }}>
            <select
              value={billing.country}
              onChange={(e) => {
                updateField("country", e.target.value);
                if (!STATE_REQUIRED_COUNTRIES.has(e.target.value)) {
                  setBilling((prev) => ({ ...prev, stateRegion: "" }));
                }
              }}
              style={{
                width: "100%", padding: "14px 16px",
                background: "rgba(255, 255, 255, 0.12)",
                border: `1px solid ${fieldErrors.country ? "#e47a6a" : "rgba(255, 255, 255, 0.2)"}`,
                borderRadius: 8, color: billing.country ? "#fff" : "rgba(255, 255, 255, 0.8)",
                fontFamily: "'Raleway', sans-serif", fontSize: "0.9375rem",
                boxSizing: "border-box", appearance: "none", cursor: "pointer",
              }}
            >
              <option value="" style={{ background: "#513a28", color: "#fff" }}>Country</option>
              {COUNTRIES.map((c) => <option key={c} value={c} style={{ background: "#513a28", color: "#fff" }}>{c}</option>)}
            </select>
          </InputGroup>
          {STATE_REQUIRED_COUNTRIES.has(billing.country) && (() => {
            const list = billing.country === "Canada" ? CA_PROVINCES : US_STATES;
            return (
              <InputGroup style={{ marginBottom: "20px" }}>
                <select
                  value={billing.stateRegion}
                  onChange={(e) => updateField("stateRegion", e.target.value)}
                  style={{
                    width: "100%", padding: "14px 16px",
                    background: "rgba(255, 255, 255, 0.12)",
                    border: `1px solid ${fieldErrors.stateRegion ? "#e47a6a" : "rgba(255, 255, 255, 0.2)"}`,
                    borderRadius: 8, color: billing.stateRegion ? "#fff" : "rgba(255, 255, 255, 0.8)",
                    fontFamily: "'Raleway', sans-serif", fontSize: "0.9375rem",
                    boxSizing: "border-box", appearance: "none", cursor: "pointer",
                  }}
                >
                  <option value="" style={{ background: "#513a28", color: "#fff" }}>
                    {billing.country === "Canada" ? "Province" : "State"}
                  </option>
                  {list.map((s) => (
                    <option key={s.code} value={s.code} style={{ background: "#513a28", color: "#fff" }}>{s.name}</option>
                  ))}
                </select>
              </InputGroup>
            );
          })()}
          <FormRow>
            <InputGroup><InputField type="text" placeholder="Company (optional)" value={billing.companyName} onChange={(e) => updateField("companyName", e.target.value)} $error={!!fieldErrors.companyName} /></InputGroup>
            <InputGroup><InputField type="text" placeholder="VAT ID (optional)" value={billing.vatId} onChange={(e) => updateField("vatId", e.target.value)} $error={!!fieldErrors.vatId} /></InputGroup>
          </FormRow>
        </ShakeWrap>

        <SectionLabel style={{ marginTop: 24 }}><span>Card Details</span></SectionLabel>
        <div style={{ background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(235, 194, 142, 0.25)", borderRadius: 12, padding: 16, marginBottom: 12 }}>
          <PaymentElement
            onReady={() => setReady(true)}
            onChange={(e: any) => {
              if (error) setError(null);
              setPaymentComplete(!!e?.complete);
            }}
            options={{
              layout: "tabs",
              fields: { billingDetails: "never" },
              wallets: { applePay: "auto", googlePay: "auto", link: "auto" },
            }}
          />
        </div>

        {error && (
          <div ref={errorRef} style={{
            padding: "10px 14px", borderRadius: 8,
            background: "rgba(228, 122, 106, 0.12)", border: "1px solid rgba(228, 122, 106, 0.3)",
            color: "#e47a6a", fontSize: "0.875rem", fontFamily: "'Raleway', sans-serif",
            marginBottom: 16,
          }}>{error}</div>
        )}
      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <Actions>
          <BackBtn onClick={() => dispatch(setCheckoutStep("upgrade-review"))} disabled={paying || finalizing}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><polyline points="12 19 5 12 12 5" />
            </svg>
            {" "}Back
          </BackBtn>
          <PrimaryBtn onClick={handleSubmit} disabled={paying || finalizing || !ready || !paymentComplete} $dimmed={paying || finalizing || !paymentComplete}>
            {finalizing ? <><Spinner />Finalizing...</> : paying ? <><Spinner />Processing...</> : `Pay $${payAmount.toFixed(2)} Now`}
          </PrimaryBtn>
        </Actions>
      </SlidePanelFooter>
    </>
  );
};

/** Wrapper that mounts Stripe Elements in setup mode so the inner form can
 *  collect a new PaymentMethod for this upgrade. */
const UpgradePaymentView: React.FC = () => {
  const { subscriptionUpgrade } = useAppSelector((state) => state.cart);
  if (!subscriptionUpgrade) return null;

  return (
    <Elements
      stripe={getStripe()}
      options={{
        mode: "setup",
        currency: "usd",
        // Must match the SetupIntent's payment_method_types — backend locks
        // it to card + link, and Stripe rejects confirmation if Elements
        // collected via automatic_payment_methods instead. Removing the
        // manual flag too: confirmSetup auto-creates the PM, no separate
        // createPaymentMethod step needed.
        paymentMethodTypes: ["card", "link"],
        appearance: {
          theme: "flat",
          variables: {
            colorPrimary: "#ebc28e",
            colorBackground: "#513a28",
            colorText: "#ffffff",
            colorTextSecondary: "rgba(251, 244, 236, 0.5)",
            colorTextPlaceholder: "rgba(255, 255, 255, 0.7)",
            colorDanger: "#e47a6a",
            colorIcon: "#ebc28e",
            colorIconHover: "#f7ede6",
            fontFamily: "'Raleway', sans-serif",
            fontSizeBase: "0.9375rem",
            borderRadius: "8px",
            spacingUnit: "4px",
            focusBoxShadow: "none",
            focusOutline: "none",
          },
          rules: {
            // The Link express-checkout panel (when expanded) renders as a
            // `.Block`. Default theming gives it a brown slightly different
            // from our card-details container, which reads as an obvious
            // shade mismatch. Strip the fill, keep a faint border so the
            // panel still groups visually but blends with its surroundings.
            ".Block": {
              backgroundColor: "transparent",
              border: "1px solid rgba(255, 255, 255, 0.15)",
            },
          },
        },
      }}
    >
      <UpgradePaymentForm />
    </Elements>
  );
};

export default UpgradePaymentView;
